import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { showSnackbar } from 'hooks/useSnackbar';
import { wrapper } from 'middlewares/generator/wrapper';
import { accountIdSelector } from 'store/user/selectors';
import {
  ASSET_DEFAULT_MAX_SIZE,
  ASSET_RECOMMENDED_MAX_SIZE,
  ASSET_STORAGE_TYPES,
  mapMime2AssetType,
} from 'utils/asset';
import api, { upload } from 'utils/axios';
import { decamelizeKeys } from 'utils/humps';

import { openSelector, settingsSelector } from './selectors';
import { ASSET_TYPE_EDIT, ASSET_TYPE_UPLOAD, ASSET_TYPE_VIEW } from './types';

import {
  changeSettingsAction,
  closeModalAction,
  onBackAction,
  onDeleteAction,
  onEditAction,
  onSaveAction,
  onUploadAction,
  openModalAction,
} from './index';

const STORAGE_TYPES_TO_PATH_SEGMENT_MAP = {
  [ASSET_STORAGE_TYPES.ASSETS]: 'assets',
  [ASSET_STORAGE_TYPES.DOCUMENTS]: 'documents',
};

const isClosedSelector = (state) => openSelector(state) === null;
const withAcc = (id, path) => `/acc/${id}/${path}`;

export const viewThunk = ({ id, storageType = ASSET_STORAGE_TYPES.ASSETS, options }) =>
  wrapper(async function* (dispatch, getState) {
    const accId = accountIdSelector(getState());
    const asset = (await api.get(withAcc(accId, STORAGE_TYPES_TO_PATH_SEGMENT_MAP[storageType] + '/' + id))).data;

    dispatch(openModalAction({ type: ASSET_TYPE_VIEW, settings: asset, options }));

    yield async () => isClosedSelector(getState());
  });

export const editThunk = ({
  id,
  saveButtonText = 'Save',
  tagsPlaceholder = 'Enter tags here',
  namePlaceholder = 'Enter a name',
  modalTitle = 'Manage Media',
  searchPlaceholder = 'Search',
  isCenterOfImageEnabled = true,
  withoutClosing = false,
  withTags = true,
  withAssetSettings = true,
  onDelete = () => {},
  onCancel = () => {},
  onAfterEdited,
  storageType = ASSET_STORAGE_TYPES.ASSETS,
  ...settings
}) =>
  wrapper(async function* (dispatch, getState) {
    const accId = accountIdSelector(getState());
    const asset = (await api.get(withAcc(accId, STORAGE_TYPES_TO_PATH_SEGMENT_MAP[storageType] + '/' + id))).data;

    dispatch(
      openModalAction({
        type: ASSET_TYPE_EDIT,
        settings: {
          asset,
          saveButtonText,
          tagsPlaceholder,
          namePlaceholder,
          modalTitle,
          searchPlaceholder,
          isCenterOfImageEnabled,
          withTags,
          withAssetSettings,
          ...settings,
        },
      }),
    );

    yield async (action) => {
      if (isClosedSelector(getState())) {
        await onCancel();
        return true;
      }

      if (action.type === onBackAction.type) {
        return true;
      }

      if (action.type === onDeleteAction.type) {
        await onDelete(settingsSelector(getState()).asset);
        return true;
      }

      if (action.type === onSaveAction.type) {
        if (Object.keys(action.payload.errors || {}).length) {
          dispatch(
            changeSettingsAction({
              errors: action.payload.errors,
            }),
          );

          return false;
        }

        dispatch(changeSettingsAction({ loading: true }));
        try {
          const asset = action.payload.isMine
            ? (
                await api.patch(
                  withAcc(accId, STORAGE_TYPES_TO_PATH_SEGMENT_MAP[storageType] + '/' + id),
                  settingsSelector(getState()).data,
                )
              ).data
            : { ...settingsSelector(getState()).asset, ...settingsSelector(getState()).data };

          dispatch(changeSettingsAction({ asset, data: {} }));

          if (onAfterEdited) onAfterEdited(asset);
        } catch (err) {
          let message = err?.message || GENERAL_ERROR_MESSAGE;

          if (err?.errors?.source?.['']) {
            message = 'File processing in progress. Give us a minute and try again later.';
          }

          showSnackbar({ text: message, type: SNACKBAR_SKIN.ERROR });
          dispatch(changeSettingsAction({ loading: false }));

          return false;
        }

        return true;
      }
    };

    if (!isClosedSelector(getState()) && !withoutClosing) {
      dispatch(closeModalAction());
    }
  });

export const uploadThunk = ({
  type,
  pageId = 0,
  defaults = {}, // { name, tags, primary, public }
  storageType = ASSET_STORAGE_TYPES.ASSETS,

  saveButtonText = 'Save',
  multipleSaveButtonText = 'Save and Done',
  nextButtonText = 'Continue',
  tagsPlaceholder = 'Enter tags here',
  namePlaceholder = 'Enter a name',
  dropZonePlaceholder = 'Click to Upload File',
  modalTitle = 'Add New File',
  searchPlaceholder = 'Search',
  pageName = 'Unknown page',
  notFoundText = 'No Assets Found. Please try again.',
  selectedTextMultiple = 'Successfully Added Assets',
  selectedText = 'Successfully Added Asset',
  isSelectedEnabled = false,
  isSuggestedEnabled = false,
  isSearchEnabled = false,
  isPrimaryPhoto = false,
  isCenterOfImageEnabled = true,
  isSortingEnabled = false,
  isRoomSuggested = false,
  withTags = true,
  withAssetSettings = true,
  maxFileSize = ASSET_DEFAULT_MAX_SIZE,
  recommendedFileSize = ASSET_RECOMMENDED_MAX_SIZE,
  fileExtraParams = {},
  infoAlertText,
  ...settings
}) =>
  wrapper(async function* (dispatch, getState) {
    const accId = accountIdSelector(getState());

    dispatch(
      openModalAction({
        type: ASSET_TYPE_UPLOAD,
        settings: {
          type,
          defaults,
          saveButtonText,
          multipleSaveButtonText,
          nextButtonText,
          tagsPlaceholder,
          namePlaceholder,
          dropZonePlaceholder,
          modalTitle,
          searchPlaceholder,
          pageName,
          pageId,
          notFoundText,
          selectedText,
          selectedTextMultiple,
          isSelectedEnabled,
          isSuggestedEnabled,
          isSearchEnabled,
          isPrimaryPhoto,
          isCenterOfImageEnabled,
          isSortingEnabled,
          isRoomSuggested,
          withTags,
          withAssetSettings,
          maxFileSize,
          recommendedFileSize,
          infoAlertText,
          ...settings,
        },
      }),
    );

    const uploadAssets = async () => {
      const result = [];

      dispatch(changeSettingsAction({ loading: true }));

      const settings = settingsSelector(getState());

      for (let i = 0; i < settings.files.length; i++) {
        dispatch(
          changeSettingsAction({
            loading: Math.round((i / settings.files.length) * 100) + '%',
          }),
        );

        const data = new FormData();
        const normalized = decamelizeKeys({
          ...settings.data[i],
          ...fileExtraParams,
          tags: settings?.data?.[i].tags ?? [],
        });

        for (const key in normalized) {
          // eslint-disable-next-line
          if (normalized.hasOwnProperty(key)) {
            if (Array.isArray(normalized[key])) {
              for (const item of normalized[key]) {
                data.append(key + '[]', item);
              }
            } else {
              data.append(key, normalized[key]);
            }
          }
        }

        const blob = await fetch(settings.files[i].url).then((r) => r.blob());

        data.append('file', new File([blob], settings.files[i].filename));
        data.append('type', mapMime2AssetType(settings.files[i].mimeType));

        try {
          const response = await upload(withAcc(accId, STORAGE_TYPES_TO_PATH_SEGMENT_MAP[storageType]), 'POST', data);

          result.push(response.data);
        } catch (error) {
          dispatch(changeSettingsAction({ loading: false }));

          // const message = error?.errors?.title || GENERAL_ERROR_MESSAGE;
          const message = 'Ooops. Seems the file you are uploading is broken. Try to upload another one';
          showSnackbar({ text: message, type: SNACKBAR_SKIN.ERROR });

          throw error;
        }
      }

      if (settings.multiple) {
        result.unshift(...(settingsSelector(getState()).selected || []));
      }

      await dispatch(
        changeSettingsAction({
          loading: false,
          files: [],
          data: [],
          selected: result,
          index: 0,
        }),
      );

      return result;
    };

    let interrupted;
    let needToSkip = false;

    yield async (action) => {
      if (needToSkip === true) {
        return false;
      }

      if (isClosedSelector(getState())) {
        return (interrupted = true);
      }

      // eslint-disable-next-line default-case
      switch (action.type) {
        case onDeleteAction.type: {
          return (interrupted = true);
        }
        case onEditAction.type: {
          needToSkip = true;

          const asset = action.payload.asset;
          const settings = settingsSelector(getState());

          await dispatch(
            editThunk({
              ...settings,
              id: asset.id,
              modalTitle: asset.name,
              withoutClosing: true,
            }),
          );

          const uploadedAsset = settingsSelector(getState()).asset;

          dispatch(
            openModalAction({
              type: ASSET_TYPE_UPLOAD,
              settings: {
                ...settings,
                selected: (() => {
                  if (!uploadedAsset) {
                    return settings.selected;
                  }

                  const copy = [...settings.selected];
                  const index = copy.findIndex((c) => c.id === uploadedAsset.id);

                  copy[index] = uploadedAsset;

                  return copy;
                })(),
              },
            }),
          );

          needToSkip = false;

          return false;
        }
        case onSaveAction.type: {
          return true;
        }
        case onUploadAction.type: {
          try {
            await uploadAssets();
            return !settingsSelector(getState()).isSelectedEnabled;
          } catch {
            return false;
          }
        }
      }
    };

    if (!isClosedSelector(getState())) {
      dispatch(closeModalAction());
    }

    if (interrupted) {
      return null;
    }

    return settingsSelector(getState()).selected;
  });
